import type { HeldStock } from '@repo/desktop-bridge';

import { type StockedItem, stockNoteFor } from './stock-position';

/**
 * Where a basket line's steppers and typed box stop, or `null` where they do not
 * (#295).
 *
 * **The control, not the judgement, and kept out of `stock-position.ts` for
 * that reason.** That module answers what the records say and refuses nothing
 * (CL-801). This is the one place a figure from it sets how far a control goes
 * — and going further is still one deliberate act on the line, never a refusal,
 * so a stale position cannot stop the park trading.
 *
 * Read off `stockNoteFor`'s own `kind`s rather than a threshold of its own, by
 * asking what the records would say of a basket that wanted everything:
 *
 * - **`short`** — the recorded level is the ceiling.
 * - **`none-left`** — nought. A line cannot hold nought, so the screen holds it
 *   at the one it was rung up as.
 * - **`not-stocked`, `behind`, and the silences** (untracked, no position) — no
 *   ceiling. A zero there would turn "we do not carry it", "the records owe the
 *   shelf" and "there is nothing to say" into an empty shelf.
 */
export function recordedCeiling(item: StockedItem, held: HeldStock | null): number | null {
  const note = stockNoteFor(item, Number.POSITIVE_INFINITY, held);

  switch (note?.kind) {
    case 'short':
      return note.level;

    case 'none-left':
      return 0;

    default:
      return null;
  }
}
